/**
 * Shared upload helper for TipTap media (image, audio, etc.).
 * Uses the same global upload endpoint configuration as content-core/upload.
 */

import { normalizeStorageMediaUrl } from '@ai-content-hub/content-core';

/* ------------------------------------------------------------------ */
/* Upload endpoint resolution                                          */
/* ------------------------------------------------------------------ */

interface UploadWin {
    __UPLOAD_ENDPOINT__?: string;
    __UPLOAD_GET_HEADERS__?: () => Record<string, string>;
}

export function getUploadEndpoint(): string {
    const win = typeof window !== 'undefined' ? (window as unknown as UploadWin) : undefined;
    if (win?.__UPLOAD_ENDPOINT__) return win.__UPLOAD_ENDPOINT__;
    try {
        const env = (import.meta as unknown as { env?: Record<string, string> }).env;
        if (env?.VITE_UPLOAD_ENDPOINT) return env.VITE_UPLOAD_ENDPOINT;
        const apiUrl = env?.VITE_API_URL || env?.VITE_API_BASE_URL || env?.VITE_AI_API_URL;
        if (apiUrl) {
            const base = apiUrl.replace(/\/+$/, '');
            return base.includes('/api') ? `${base}/upload` : `${base}/api/upload`;
        }
    } catch {
        /* env not available */
    }
    return '/api/upload';
}

/* ------------------------------------------------------------------ */
/* File upload                                                          */
/* ------------------------------------------------------------------ */

export interface UploadResult {
    url?: string;
    error?: string;
}

export async function uploadFile(file: File): Promise<UploadResult> {
    const win = typeof window !== 'undefined' ? (window as unknown as UploadWin) : undefined;
    const endpoint = getUploadEndpoint();
    const authHeaders = win?.__UPLOAD_GET_HEADERS__?.() ?? {};

    const formData = new FormData();
    formData.append('file', file);

    try {
        const res = await fetch(endpoint, {
            method: 'POST',
            headers: authHeaders,
            body: formData,
        });
        if (!res.ok) {
            const text = await res.text();
            try {
                const json = JSON.parse(text) as Record<string, unknown>;
                return {
                    error:
                        (typeof json.error === 'string' && json.error) ||
                        (typeof json.message === 'string' && json.message) ||
                        `Upload failed (${res.status})`,
                };
            } catch {
                return { error: `Upload failed (${res.status})` };
            }
        }
        const json = (await res.json()) as Record<string, unknown>;
        const inner =
            json.data && typeof json.data === 'object'
                ? (json.data as Record<string, unknown>)
                : json;
        const url =
            (typeof inner.url === 'string' && inner.url) ||
            (typeof json.url === 'string' && json.url) ||
            (typeof json.fileUrl === 'string' && json.fileUrl) ||
            undefined;
        return { url: url ? normalizeStorageMediaUrl(url) : undefined };
    } catch (err) {
        return { error: err instanceof Error ? err.message : 'Network error' };
    }
}

/* ------------------------------------------------------------------ */
/* Image intrinsic-size measurement                                     */
/* ------------------------------------------------------------------ */

/**
 * EAA-1736: Default cap for a freshly inserted image's width (px). Uploaded
 * images are inserted at their own intrinsic width, but capped to this value so
 * a large photo does not stretch to the full editor column ("full size"). The
 * author can still drag the resize handles afterwards.
 */
export const DEFAULT_INSERT_IMAGE_MAX_WIDTH = 480;

/** Does this source look like an SVG (File type/name or an svg data-URL)? */
function isSvgSource(source: File | string): boolean {
    if (typeof source === 'string') {
        return source.startsWith('data:image/svg+xml');
    }
    return source.type === 'image/svg+xml' || /\.svg$/i.test(source.name);
}

/** Read the raw SVG markup from a File or an svg data-URL (null if not svg / unreadable). */
async function readSvgText(source: File | string): Promise<string | null> {
    try {
        if (typeof source !== 'string') {
            return await source.text();
        }
        const comma = source.indexOf(',');
        if (comma < 0) return null;
        const meta = source.slice(0, comma);
        const data = source.slice(comma + 1);
        if (meta.includes(';base64')) {
            return typeof atob === 'function' ? atob(data) : null;
        }
        return decodeURIComponent(data);
    } catch {
        return null;
    }
}

/**
 * EAA-1736: Parse an SVG's intrinsic width from its markup. `<img>.naturalWidth`
 * is 0 for a viewBox-only SVG (no width/height attrs), which is why such icons
 * balloon to the browser's 300px default when inserted. Prefer a plain/px `width`
 * attribute, else fall back to the third viewBox value. Returns null when neither
 * is a usable pixel size (e.g. percentage width).
 */
function parseSvgWidth(svgText: string, maxWidth: number): number | null {
    if (typeof DOMParser === 'undefined') return null;
    try {
        const doc = new DOMParser().parseFromString(svgText, 'image/svg+xml');
        const svg = doc.documentElement;
        if (!svg || svg.nodeName.toLowerCase() !== 'svg') return null;

        const widthAttr = svg.getAttribute('width');
        if (widthAttr && /^\s*\d*\.?\d+(px)?\s*$/.test(widthAttr)) {
            const px = parseFloat(widthAttr);
            if (px > 0) return Math.min(px, maxWidth);
        }

        const viewBox = svg.getAttribute('viewBox');
        if (viewBox) {
            const parts = viewBox.split(/[\s,]+/).map(Number);
            if (parts.length === 4 && Number.isFinite(parts[2]) && parts[2] > 0) {
                return Math.min(parts[2], maxWidth);
            }
        }
    } catch {
        /* malformed svg — fall through */
    }
    return null;
}

/**
 * EAA-1736: Measure an image's intrinsic width and return a value suitable for
 * the `width` node attribute — the natural width capped at `maxWidth`.
 *
 * WHY: On insert we know only the (protected) storage URL, which is not loadable
 * without a signed URL, so callers pass the original File / data-URL here to read
 * the real pixel size. Without this the image node has no width and the browser
 * renders large images at the full container width (and viewBox-only SVGs at the
 * 300px default). Resolves to null when the size cannot be read, so the caller
 * leaves the width unset (old behaviour).
 */
export async function measureImageWidth(
    source: File | string,
    maxWidth: number = DEFAULT_INSERT_IMAGE_MAX_WIDTH
): Promise<number | null> {
    if (typeof window === 'undefined') return null;

    // SVGs frequently expose no intrinsic pixel size to <img>; parse the markup.
    if (isSvgSource(source)) {
        const svgText = await readSvgText(source);
        const svgWidth = svgText != null ? parseSvgWidth(svgText, maxWidth) : null;
        if (svgWidth != null) return svgWidth;
        // Bounded fallback so a dimensionless SVG is not inserted at full/300px size.
        return maxWidth;
    }

    if (typeof window.Image === 'undefined') return null;
    const objectUrl = typeof source === 'string' ? null : URL.createObjectURL(source);
    const src = objectUrl ?? (source as string);
    return new Promise((resolve) => {
        const img = new window.Image();
        const done = (width: number | null) => {
            if (objectUrl) URL.revokeObjectURL(objectUrl);
            resolve(width);
        };
        img.onload = () => {
            const natural = img.naturalWidth || 0;
            done(natural > 0 ? Math.min(natural, maxWidth) : null);
        };
        img.onerror = () => done(null);
        img.src = src;
    });
}
